import storyblokImageSize from './storyblokImageSize'

// @nuxt/image emits one srcset candidate per screen named in `sizes`, per
// density — so the largest candidate is `largest named screen x max density`
// (3072 with the default screens and densities [1, 2]). Nothing in that chain
// looks at the upload's real width, and Cloudflare upscales happily: the
// homepage hero is a 1920px-wide asset that was shipping a 3072-wide, 672 KB
// AVIF, versus 308 KB for the same image capped at its own 1920.
//
// So build the ladder from the source instead: every configured breakpoint
// below the upload's width, then one final candidate at exactly that width.
// A retina desktop asking for 3024px now gets the 1920 original and scales it
// up locally, which is the same pixels the CDN would have invented — minus the
// bytes. Density is deliberately left at 1x by the caller: with `w` descriptors
// the browser already multiplies the `sizes` slot by its own DPR, so a second
// density only duplicates the ladder at double the width.
//
// Only for images the design paints at 100vw at EVERY breakpoint. A component
// whose slot is capped (`md:960px`) must not use this: the ladder claims 100vw
// throughout, so on a big upload it would serve a source-width candidate into a
// slot a third of that size — worse than what it replaces.
export default function heroImageSizes(filename, ratio, screens) {
    const dims = storyblokImageSize(filename)
    if (!dims)
        return null

    const breakpoints = [...new Set(Object.values(screens))].sort((a, b) => a - b)

    // Never exceed what the default ladder would already have produced
    // (largest breakpoint x 2), so an oversized upload cannot make things worse
    // than the behaviour this replaces.
    const width = Math.min(dims.width, breakpoints.at(-1) * 2)
    const ladder = breakpoints.filter(screen => screen < width)

    return {
        width,
        height: Math.round(width / ratio),
        // Numeric keys are legal: getSizesVariant falls back to
        // Number.parseInt(key) for anything absent from `screens`, which is how
        // the final source-width candidate gets in without a global breakpoint.
        sizes: [...ladder, width].map(screen => `${screen}:100vw`).join(' '),
    }
}
